import { format } from "date-fns";

import { faUser } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { BlacklistReasonPopover } from "@/pages/Private/pages/DoNotContact/components/BlacklistReasonPopover";
import {
	blacklistDetectedByLabelKey,
	blacklistOriginLabelKey,
	blacklistOriginPillClass,
} from "@/pages/Private/pages/DoNotContact/utils/blacklistOriginMeta";

import { ListPerson } from "../../schema/list";

/**
 * The blacklist columns of a list's Blacklist bucket (PREC-3594).
 *
 * The list used to say only *that* a lead was blocked — a warning icon and nothing else — so the
 * user had to leave for Contacts → Blacklist to find out why. These render the same four facts the
 * Blacklist tab shows, from the same helpers, so a lead reads identically in both places.
 *
 * `ts` resolves keys under the `contacts.` namespace: the wording is shared with the Blacklist tab
 * on purpose, and duplicating it into the inbox namespace is how the two drift apart.
 */
type ContactsTranslator = (key: string) => string;

const emptyCell = () => <span className="text-sm text-text-meta">-</span>;

/** The blacklist type, with the reasoning behind it on hover. */
export const renderBlacklistTypeColumn = (ts: ContactsTranslator): TableColumn => ({
	title: ts("blacklist.origin"),
	width: "fit-content",
	minWidth: "180px",
	uid: "blacklistType",
	hideable: true,
	moveable: true,
	show: false,
	render: (row: ListPerson) => {
		const detail = row.blacklistDetail;

		if (!detail?.origin) {
			return emptyCell();
		}

		const label = ts(blacklistOriginLabelKey(detail.origin));
		const detectedByKey = blacklistDetectedByLabelKey(detail.detectedBy);
		const detectedBy = detectedByKey ? ts(detectedByKey) : null;
		const attribution = detail.addedBy
			? [detectedBy, detail.addedBy].filter(Boolean).join(" · ")
			: detectedBy;

		return (
			<Tooltip
				content={
					<BlacklistReasonPopover
						attribution={attribution}
						reasoning={detail.reason}
						typeLabel={label}
					/>
				}
				delay={300}
				disabled={!detail.reason}
				panel
			>
				<span
					className={classNames(
						"inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-xs",
						blacklistOriginPillClass(detail.origin)
					)}
				>
					{label}
				</span>
			</Tooltip>
		);
	},
});

/** AI-detected, a person, or the lead themselves. */
export const renderBlacklistDetectedByColumn = (ts: ContactsTranslator): TableColumn => ({
	title: ts("blacklist.detectedBy"),
	width: "fit-content",
	minWidth: "150px",
	uid: "blacklistDetectedBy",
	hideable: true,
	moveable: true,
	show: false,
	render: (row: ListPerson) => {
		const labelKey = blacklistDetectedByLabelKey(row.blacklistDetail?.detectedBy);

		if (!labelKey) {
			return emptyCell();
		}

		return (
			<span className="inline-flex items-center gap-1.5 text-sm text-text">
				<Icon
					className="text-text-meta"
					icon={row.blacklistDetail?.detectedBy === "AI" ? faSparkles : faUser}
				/>
				{ts(labelKey)}
			</span>
		);
	},
});

export const renderBlacklistAddedByColumn = (ts: ContactsTranslator): TableColumn => ({
	title: ts("blacklist.addedBy"),
	width: "fit-content",
	minWidth: "160px",
	uid: "blacklistAddedBy",
	hideable: true,
	moveable: true,
	show: false,
	render: (row: ListPerson) =>
		row.blacklistDetail?.addedBy ? (
			<span className="text-sm text-text">{row.blacklistDetail.addedBy}</span>
		) : (
			emptyCell()
		),
});

export const renderBlacklistAddedAtColumn = (ts: ContactsTranslator): TableColumn => ({
	title: ts("blacklist.addedToBlacklistAt"),
	width: "fit-content",
	minWidth: "160px",
	uid: "blacklistAddedAt",
	hideable: true,
	moveable: true,
	show: false,
	render: (row: ListPerson) => {
		const addedAt = row.blacklistDetail?.addedAt;

		return addedAt ? (
			<span className="text-sm text-text">{format(new Date(addedAt), "HH:mm dd.MM.yyyy")}</span>
		) : (
			emptyCell()
		);
	},
});

/** All four, in the order the ticket asks for them. */
export const renderBlacklistDetailColumns = (ts: ContactsTranslator): TableColumn[] => [
	renderBlacklistTypeColumn(ts),
	renderBlacklistDetectedByColumn(ts),
	renderBlacklistAddedByColumn(ts),
	renderBlacklistAddedAtColumn(ts),
];

/** The uids of those columns, so the table can show them only in the Blacklist bucket. */
export const BLACKLIST_DETAIL_COLUMN_UIDS = new Set([
	"blacklistType",
	"blacklistDetectedBy",
	"blacklistAddedBy",
	"blacklistAddedAt",
]);
